import React, { useState } from 'react';
import { collection, doc, writeBatch, serverTimestamp } from 'firebase/firestore';

const REQUIRED_FIELDS = {
  aircraft: ['AircraftID', 'TypeCode', 'Year', 'Make', 'Model', 'Category', 'Class', 'GearType', 'EngineType', 'Complex', 'HighPerformance', 'Pressurized'],
  experience: [
    'Date', 'AircraftID', 'From', 'To', 'Route', 'TimeOut', 'TimeIn', 'OnDuty', 'OffDuty', 'TotalTime',
    'PIC', 'SIC', 'Night', 'Solo', 'CrossCountry', 'Distance', 'DayTakeoffs', 'DayLandingsFullStop',
    'NightTakeoffs', 'NightLandingsFullStop', 'AllLandings', 'ActualInstrument', 'SimulatedInstrument',
    'HobbsStart', 'HobbsEnd', 'TachStart', 'TachEnd', 'Holds', 'Approach1', 'Approach2', 'Approach3',
    'Approach4', 'Approach5', 'Approach6', 'DualGiven', 'DualReceived', 'SimulatedFlight',
    'GroundTraining', 'InstructorName', 'InstructorComments', 'Person1', 'Person2', 'Person3', 'Person4',
    'Person5', 'Person6', 'FlightReview', 'Checkride', 'IPC', 'PilotComments'
  ],
};

const CSVImportHandler = ({ db, userId, type, onSuccess, onDataRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);

  const validateCSVFormat = (headers) => {
    const missingFields = REQUIRED_FIELDS[type].filter((field) => !headers.includes(field));
    if (missingFields.length > 0) {
      throw new Error(`Invalid CSV format: Missing required fields: ${missingFields.join(', ')}`);
    }
    return true;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setSuccessMessage(null);
    setError(null);

    try {
      const text = await file.text();
      const rows = text.split('\n').filter((row) => row.trim());
      const headers = rows[0].split(',').map((h) => h.trim());

      validateCSVFormat(headers); // Validate CSV headers

      const batch = writeBatch(db);
      const collectionRef = collection(db, `pilot/${userId}/${type}`);

      rows.slice(1).forEach((row) => {
        const cells = row.split(',').map((cell) => cell.trim());

        const data = {};
        REQUIRED_FIELDS[type].forEach((field, index) => {
          const value = cells[headers.indexOf(field)];
          if (['Complex', 'HighPerformance', 'Pressurized'].includes(field)) {
            data[field.toLowerCase()] = value === 'true'; // Convert booleans
          } else {
            data[field.toLowerCase()] = value || ''; // Default empty string
          }
        });

        data.createdAt = serverTimestamp();
        data.updatedAt = serverTimestamp();

        const docRef = doc(collectionRef);
        batch.set(docRef, data);
      });

      await batch.commit();
      setSuccessMessage(`${type === 'aircraft' ? 'Aircraft' : 'Flight Experience'} data uploaded successfully!`);
      onDataRefresh && onDataRefresh(); // Refresh data after upload
      onSuccess && onSuccess();
    } catch (err) {
      setError(`Error uploading file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="relative mt-2">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          disabled={loading}
          className="hidden"
          id={`csv-upload-${type}`}
        />
        <label
          htmlFor={`csv-upload-${type}`}
          className={`bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-blue-600 cursor-pointer ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Uploading...' : `Upload ${type === 'aircraft' ? 'Aircraft' : 'Flight Experience'} CSV`}
        </label>
      </div>
      {successMessage && <p className="text-green-500 mt-2">{successMessage}</p>}
      {error && <p className="text-red-500 mt-2">{error}</p>}
    </div>
  );
};

export default CSVImportHandler;
